import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './auth/ProtectedRoute';
import PublicRoute from './auth/PublicRoute';
import AppShell from './app/AppShell';
import LandingRedirect from './app/LandingRedirect';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPage from './pages/ForgotPage';
import ResetPage from './pages/ResetPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import BrowsePage from './pages/BrowsePage';
import PlansPage from './pages/PlansPage';
import NotFoundPage from './pages/NotFoundPage';

// The editor (konva/engine weight) is code-split: auth users never download the
// CAD chunk; the shell + sidebar + browse stay in the main bundle. The public share
// viewer pulls the same editor chunk, so it is lazy too (an anonymous visitor only
// downloads it on /s/{token}).
const EditorPage = lazy(() => import('./pages/EditorPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ShareViewerPage = lazy(() => import('./pages/ShareViewerPage'));

const lazyFallback = (
  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading…</div>
);

export default function AppRoutes() {
  return (
    <Routes>
      {/* Public (auth) routes — standalone, no shell. A signed-in user is bounced. */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot" element={<PublicRoute><ForgotPage /></PublicRoute>} />
      <Route path="/reset" element={<PublicRoute><ResetPage /></PublicRoute>} />
      <Route path="/plans" element={<PlansPage />} />

      {/* Verification is deliberately NOT wrapped in PublicRoute: a Google account
          Google would not vouch for holds a real session but an unverified email,
          and PublicRoute would bounce it to "/" — straight back into the app guard,
          which bounces it here. The page guards itself instead (verified users are
          sent on; no pending token and no session falls back to /login). */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* D1 public share viewer — UNGUARDED (no auth): a no-account visitor opens the
          live drawing read-only. The bootstrap me() 401 is swallowed, so no redirect. */}
      <Route
        path="/s/:token"
        element={
          <Suspense fallback={lazyFallback}>
            <ShareViewerPage />
          </Suspense>
        }
      />

      {/* Editor-first landing: resolve last/recent/auto-create → /editor/{id}. */}
      <Route path="/" element={<ProtectedRoute><LandingRedirect /></ProtectedRoute>} />

      {/* Persistent shell (sidebar + top bar). It stays mounted across switches;
          the main region swaps via <Outlet/>. The editor canvas + C2 sync session
          still remount per model INSIDE EditorPage (one doc per open model). */}
      <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route
          path="/editor/:modelId"
          element={<Suspense fallback={lazyFallback}><EditorPage /></Suspense>}
        />
        <Route path="/browse" element={<BrowsePage />} />
        <Route
          path="/settings"
          element={<Suspense fallback={lazyFallback}><SettingsPage /></Suspense>}
        />
        {/* Admin console — rendered inside the shell; the page guards on the user's
            is_admin and every /admin/* API route 404s non-admins (real boundary). */}
        <Route
          path="/admin"
          element={<Suspense fallback={lazyFallback}><AdminPage /></Suspense>}
        />
      </Route>

      {/* Back-compat: the old dashboard path now points at /browse. */}
      <Route path="/dashboard" element={<Navigate to="/browse" replace />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
